import { useEffect, useState } from 'react';

import { COOKIE_KEYS } from '~/common/data/cookie-keys.data.ts';

/** 사용자가 고른 화면 모드. `auto` 는 화면 폭이 정한다(고른 적 없음). */
export type ViewMode = 'auto' | 'desktop' | 'mobile';

const STORAGE_KEY = COOKIE_KEYS.VIEW_MODE;

/** 같은 탭 안의 다른 구독자에게 변경을 알린다 — storage 이벤트는 **다른 탭**에만 간다. */
const CHANGE_EVENT = 'doddies:view-mode';

function readStored(): ViewMode {
  if (typeof window === 'undefined') return 'auto';
  const value = window.localStorage.getItem(STORAGE_KEY);
  return value === 'desktop' || value === 'mobile' ? value : 'auto';
}

/**
 * 화면 모드 — 폭 판정을 사용자가 덮어쓴다.
 *
 * @description 기본은 `auto` 이고, 그때는 `useMinWidth` 의 폭 판정을 그대로 따른다. 대부분은
 *  이 상태로 쓴다. 다만 지도사가 휴대폰으로 정산 표를 넓게 보고 싶을 때처럼, 폭이 정한 답이
 *  그 순간의 의도와 어긋나는 경우가 있다. 그때만 사용자가 직접 고른다.
 *
 *  **기기(User-Agent)로 감지하지 않는다.** 이건 감지가 아니라 선택이다 — 사람이 고른 값이라
 *  틀릴 일이 없고, 언제든 되돌릴 수 있다.
 *
 *  선택은 브라우저에 남는다(localStorage). 서버가 없는 정적 호스팅이라 계정에 붙일 자리가 없고,
 *  붙인다 해도 "이 기기에서 어떻게 볼지"는 계정이 아니라 기기에 매이는 설정이다.
 */
function useViewMode(): [ViewMode, (mode: ViewMode) => void] {
  const [mode, setMode] = useState<ViewMode>(readStored);

  useEffect(() => {
    const sync = () => setMode(readStored());

    // 다른 탭에서 바꾼 경우(storage) + 같은 탭의 다른 컴포넌트가 바꾼 경우(커스텀 이벤트).
    window.addEventListener('storage', sync);
    window.addEventListener(CHANGE_EVENT, sync);
    return () => {
      window.removeEventListener('storage', sync);
      window.removeEventListener(CHANGE_EVENT, sync);
    };
  }, []);

  const change = (next: ViewMode) => {
    if (next === 'auto') window.localStorage.removeItem(STORAGE_KEY);
    else window.localStorage.setItem(STORAGE_KEY, next);
    window.dispatchEvent(new Event(CHANGE_EVENT));
  };

  return [mode, change];
}

export default useViewMode;
